/**
 * Centralized Company Logo Service
 *
 * - ALL fetching happens in prefetchLogos() – runs once on contacts load
 * - Components ONLY read from cache (zero network, zero speed impact)
 * - Uses requestIdleCallback so fetching never blocks UI
 * - Persists in localStorage (survives reloads – no re-fetching)
 * - Subscribers pattern: components get notified when their logo is ready
 */

const STORAGE_KEY = 'bm_logo_cache'
const MIN_LOGO_SIZE = 20          // px – reject default globe icons
const CACHE_TTL = 7 * 24 * 3600e3 // 7 days
const IMG_TIMEOUT = 4000          // ms – per-provider timeout

// ── In-memory cache ──
let _memCache = null   // domain → { url: string|null, ts: number }
let _inflight = {}     // domain → Promise<string|null>
let _saveTimer = null

// ── Subscriber callbacks: domain → Set<callback> ──
const _subscribers = {}

// ── localStorage helpers ──
function loadFromStorage() {
  if (_memCache) return _memCache
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      const now = Date.now()
      const clean = {}
      for (const [domain, entry] of Object.entries(parsed)) {
        if (now - entry.ts < CACHE_TTL) clean[domain] = entry
      }
      _memCache = clean
      return _memCache
    }
  } catch { /* corrupted – start fresh */ }
  _memCache = {}
  return _memCache
}

function saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(_memCache))
  } catch { /* quota exceeded – silent */ }
}

function scheduleSave() {
  clearTimeout(_saveTimer)
  _saveTimer = setTimeout(saveToStorage, 500)
}

// ── Domain extraction ──
const PUBLIC_SUFFIX_2 = new Set([
  'co.uk', 'com.au', 'com.br', 'com.ar', 'com.mx', 'com.tr',
  'com.sg', 'com.hk', 'co.jp', 'co.kr', 'co.za', 'co.in',
  'com.cn', 'com.tw', 'gov.uk'
])

export function extractDomain(email) {
  if (!email) return null
  const raw = _rawDomain(email)
  if (!raw) return null
  return _registrable(raw)
}

export function extractDomainCandidates(email) {
  if (!email) return []
  const raw = _rawDomain(email)
  if (!raw) return []
  const primary = _registrable(raw)
  if (!primary) return []
  const candidates = [primary]
  if (raw !== primary) candidates.push(raw)
  return candidates
}

function _rawDomain(email) {
  const tokens = email.replace(/^mailto:/i, '').replace(/[<>]/g, ' ').split(/\s+/).filter(Boolean)
  let domainPart = null
  for (const t of tokens) {
    const at = t.lastIndexOf('@')
    if (at !== -1 && at < t.length - 1) { domainPart = t.slice(at + 1); break }
  }
  if (!domainPart) return null
  return domainPart.toLowerCase().replace(/^\.+/, '').replace(/\.+$/, '').split(':')[0].split('/')[0] || null
}

function _registrable(raw) {
  const stripped = raw.replace(/^(mail|mg|smtp|email|bounce|news|mailer|send|noreply|no-reply)\./, '')
  const parts = stripped.split('.').filter(Boolean)
  if (parts.length < 2) return null
  const lastTwo = parts.slice(-2).join('.')
  if (PUBLIC_SUFFIX_2.has(lastTwo) && parts.length >= 3) return parts.slice(-3).join('.')
  return lastTwo
}

// ── Image validation (private – only used inside prefetch) ──
const PROVIDERS = [
  domain => `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
  domain => `https://logo.clearbit.com/${domain}`,
  domain => `https://icon.horse/icon/${domain}`,
  domain => `https://${domain}/apple-touch-icon.png`,
  domain => `https://${domain}/favicon.ico`
]

function _validateImage(url) {
  return new Promise((resolve) => {
    const img = new Image()
    img.decoding = 'async'
    const timer = setTimeout(() => { img.src = ''; resolve(null) }, IMG_TIMEOUT)
    img.onload = () => {
      clearTimeout(timer)
      resolve(img.naturalWidth >= MIN_LOGO_SIZE && img.naturalHeight >= MIN_LOGO_SIZE ? url : null)
    }
    img.onerror = () => { clearTimeout(timer); resolve(null) }
    img.src = url
  })
}

function _fetchAndValidate(domains) {
  const attempts = []
  for (const getUrl of PROVIDERS) {
    for (const domain of domains) {
      attempts.push(getUrl(domain))
    }
  }
  return attempts.reduce(
    (chain, url) => chain.then(result => result || _validateImage(url)),
    Promise.resolve(null)
  )
}

// ── Resolve a single domain (internal, deduped) ──
function _resolveLogo(candidates) {
  const primary = candidates[0]
  if (!primary) return Promise.resolve(null)
  const cache = loadFromStorage()

  if (cache[primary] && Date.now() - cache[primary].ts < CACHE_TTL) {
    return Promise.resolve(cache[primary].url)
  }
  if (_inflight[primary]) return _inflight[primary]

  const promise = _fetchAndValidate(candidates).then((url) => {
    cache[primary] = { url, ts: Date.now() }
    delete _inflight[primary]
    scheduleSave()
    _notifySubscribers(primary, url)
    return url
  })
  _inflight[primary] = promise
  return promise
}

// ── Subscriber system ──
function _notifySubscribers(domain, url) {
  const subs = _subscribers[domain]
  if (subs) {
    subs.forEach(cb => cb(url))
    delete _subscribers[domain]
  }
}

/**
 * Subscribe to be notified when a domain's logo is resolved by prefetch.
 * Returns an unsubscribe function for cleanup.
 */
export function onLogoReady(domain, callback) {
  if (!_subscribers[domain]) _subscribers[domain] = new Set()
  _subscribers[domain].add(callback)
  return () => {
    if (_subscribers[domain]) {
      _subscribers[domain].delete(callback)
      if (!_subscribers[domain].size) delete _subscribers[domain]
    }
  }
}

// ── Public API for components (CACHE-ONLY, no network) ──

/**
 * Get cached logo synchronously. Returns url, null, or undefined (not cached yet).
 */
export function getCachedLogo(domain) {
  if (!domain) return null
  const cache = loadFromStorage()
  const entry = cache[domain]
  if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.url
  return undefined
}

/**
 * Prefetch logos for all contacts. Runs in background idle time.
 * Components never call this – it's called once from the contacts store.
 */
export function prefetchLogos(contacts) {
  if (!contacts || !contacts.length) return

  const cache = loadFromStorage()
  const toFetch = []
  const seen = new Set()

  for (const c of contacts) {
    const email = c.emailAddress || c.email
    const candidates = extractDomainCandidates(email)
    const primary = candidates[0]
    if (primary && !cache[primary] && !seen.has(primary)) {
      seen.add(primary)
      toFetch.push(candidates)
    }
  }
  if (!toFetch.length) return

  const CONCURRENCY = 2
  let idx = 0
  let active = 0

  function processNext() {
    while (active < CONCURRENCY && idx < toFetch.length) {
      const candidates = toFetch[idx++]
      active++
      _resolveLogo(candidates).finally(() => {
        active--
        scheduleNext()
      })
    }
  }

  function scheduleNext() {
    if (idx >= toFetch.length) return
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => processNext(), { timeout: 2000 })
    } else {
      setTimeout(processNext, 100)
    }
  }

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => processNext(), { timeout: 3000 })
  } else {
    setTimeout(processNext, 500)
  }
}
